import React, { useState, useEffect, useContext } from 'react';
import { Auth } from 'aws-amplify';

import AnimateHeight from 'react-animate-height';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import EditIcon from '@mui/icons-material/Edit';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { ReactComponent as LoadingSpinner } from '../../images/loading-spinner.svg';

import { ErrorMessageContext } from '../../contexts';

import { getUserEmail, getUserId, getUserPhoneNumber } from '../../utils/auth';

import useFetch from '../../hooks/useFetch';

import './BasicInfo.scss';

export default function BasicInfo() {
  const [height, setHeight] = useState('auto');

  const [editingAccountInfo, setEditingAccountInfo] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [newUserInfo, setNewUserInfo] = useState({});
  const [originalUserInfo, setOriginalUserInfo] = useState({});
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);

  const { setShowErrorMessage } = useContext(ErrorMessageContext);

  async function getUserData() {
    try {
      const userAuthData = await Auth.currentUserInfo();
      const enterpriseCompanyInfo = localStorage.getItem('enterpriseCompanyInfo');
      const parsedCompanyInfo = enterpriseCompanyInfo ? JSON.parse(enterpriseCompanyInfo) : null;

      const accountData = {
        firstName: userAuthData?.attributes?.['custom:firstName'] || '',
        lastName: userAuthData?.attributes?.['custom:lastName'] || '',
        companyName: parsedCompanyInfo?.companyName || '',
        phoneNumber: userAuthData?.attributes?.['custom:phoneNumber'] || getUserPhoneNumber(),
        userId: userAuthData?.attributes?.sub || getUserId(),
        email: userAuthData?.attributes?.email || getUserEmail(),
      };
      setUserInfo(accountData);
      setNewUserInfo(accountData);
      setOriginalUserInfo(accountData);
    } catch (e) {
      setShowErrorMessage(e.toString());
    }
    return null;
  }

  useEffect(() => { getUserData(); }, []);

  const [{ loading: accountInfoIsSaving }, updateEnterpriseUserRequest] = useFetch();

  async function updateData() {
    let hasError = false;
    if (newUserInfo.firstName === '') {
      setFirstNameError(true);
      hasError = true;
    }
    if (newUserInfo.lastName === '') {
      setLastNameError(true);
      hasError = true;
    }
    if (hasError) return;

    if (JSON.stringify(userInfo) !== JSON.stringify(newUserInfo)) {
      const userData = {
        firstName: newUserInfo.firstName,
        lastName: newUserInfo.lastName,
        email: newUserInfo.email,
        phoneNumber: newUserInfo.phoneNumber || '',
      };
      updateEnterpriseUserRequest({
        url: '/accounts/update-enterprise-user',
        method: 'post',
        body: userData,
        bodyIds: ['enterpriseCompanyId', 'requestUserId'],
        onSuccess: async () => {
          setUserInfo(newUserInfo);
          setEditingAccountInfo(false);
          
          try {
            const user = await Auth.currentAuthenticatedUser();
            await Auth.updateUserAttributes(user, {
              'custom:firstName': newUserInfo.firstName,
              'custom:lastName': newUserInfo.lastName,
              'custom:phoneNumber': newUserInfo.phoneNumber || '',
            });
          } catch (error) {
            setShowErrorMessage(error.toString());
          }
        },
        onError: (error) => {
          setShowErrorMessage(error.toString());
        }
      });
    } else {
      setEditingAccountInfo(false);
    }
  }

  const handleStartEditing = () => {
    setOriginalUserInfo(newUserInfo);
    setEditingAccountInfo(true);
  };

  const handleCancelEditing = () => {
    setNewUserInfo(originalUserInfo);
    setEditingAccountInfo(false);
    setFirstNameError(false);
    setLastNameError(false);
  };

  return (
    <main className="BasicInfo">
      <div className="info-dropdown">
        <div
          role="button"
          className="header-row"
          onClick={() => { setHeight(height ? 0 : 'auto'); }}
          onKeyDown={(e) => { if (e.key === 'Enter') setHeight(height ? 0 : 'auto'); }}
          tabIndex={0}
        >
          <IconButton
            className="chevron-btn"
            onClick={() => { setHeight(height ? 0 : 'auto'); }}
          >
            <ExpandCircleDownOutlinedIcon
              className={`circle-chevron${height ? ' upward' : ' downward'}`}
            />
          </IconButton>
          <h5>Your information</h5>
          {!editingAccountInfo && !accountInfoIsSaving ? (
            <IconButton
              className="edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleStartEditing();
              }}
            >
              <EditIcon />
            </IconButton>
          ) : (
            <div className="btns-container">
              <Button
                className="save-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  updateData();
                }}
                disabled={accountInfoIsSaving}
              >
                {!accountInfoIsSaving ? (
                  <>
                    <SaveOutlinedIcon />
                    Save
                  </>
                ) : (
                  <>
                    <LoadingSpinner className="loading-spinner" />
                    Saving...
                  </>
                )}
              </Button>
              <Button
                className="cancel-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEditing();
                }}
              >
                <CancelOutlinedIcon />
                Cancel
              </Button>
            </div>
          )}
        </div>
        <div>
          <AnimateHeight duration={500} height={height}>
            <div className="data-tiles-grid">
              {!editingAccountInfo ? (
                <div className="data-tile">
                  <span className="data-name">First name</span>
                  <span className="data">{newUserInfo.firstName}</span>
                </div>
              ) : (
                <TextField
                  label="First name"
                  value={newUserInfo.firstName}
                  onChange={(e) => {
                    setNewUserInfo({ ...newUserInfo, firstName: e.target.value });
                    setFirstNameError(false);
                  }}
                  error={firstNameError}
                  helperText={firstNameError ? "First name is required" : ""}
                  required
                />
              )}
              {!editingAccountInfo ? (
                <div className="data-tile">
                  <span className="data-name">Last name</span>
                  <span className="data">{newUserInfo.lastName}</span>
                </div>
              ) : (
                <TextField
                  label="Last name"
                  value={newUserInfo.lastName}
                  onChange={(e) => {
                    setNewUserInfo({ ...newUserInfo, lastName: e.target.value });
                    setLastNameError(false);
                  }}
                  error={lastNameError}
                  helperText={lastNameError ? "Last name is required" : ""}
                  required
                />
              )}
              <div className="data-tile">
                <span className="data-name">Email</span>
                <span className="data">{newUserInfo.email}</span>
              </div>
              {!editingAccountInfo ? (
                <div className="data-tile">
                  <span className="data-name">Phone number</span>
                  <span className="data">{newUserInfo.phoneNumber}</span>
                </div>
              ) : (
                <TextField
                  label="Phone number"
                  value={newUserInfo.phoneNumber}
                  onChange={(e) => setNewUserInfo({ ...newUserInfo, phoneNumber: e.target.value })}
                />
              )}
            </div>
          </AnimateHeight>
        </div>
      </div>
    </main>
  );
}
